import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  inject,
  computed,
  signal,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { IconComponent } from '../icon/icon.component';
import { Clipboard } from '@angular/cdk/clipboard';
import { WalletService } from '../../../core/services/wallet';
import { WalletStore } from '../../../shared/stores/wallet.store';
import { AuthService } from '../../../core/auth/auth.service';
import { PredictionStore } from '../../../features/predictions/prediction.store';
import { PortfolioStore } from '../../../features/portfolio/portfolio.store';
import { safeGetItem, safeSetItem } from '../../../core/utils/storage';

interface NavItem {
  label: string;
  route: string;
  icon: string;
  external?: boolean;
}

type SupportedLang = 'en' | 'zh-TW' | 'zh-CN' | 'ja' | 'ko' | 'es' | 'th' | 'id';

const SUPPORTED_LANGS: SupportedLang[] = ['en', 'zh-TW', 'zh-CN', 'ja', 'ko', 'es', 'th', 'id'];

const LANG_LABELS: Record<SupportedLang, string> = {
  en: 'English',
  'zh-TW': '繁體中文',
  'zh-CN': '简体中文',
  ja: '日本語',
  ko: '한국어',
  es: 'Español',
  th: 'ไทย',
  id: 'Bahasa',
};

/** Map browser language codes to our supported languages */
function detectBrowserLang(): SupportedLang {
  if (typeof navigator === 'undefined') return 'en';
  // Check localStorage first (user's previous choice) — safeGetItem
  // wraps the raw call so a missing/blocked Storage API (private mode,
  // SSR, hardened test env) degrades to null instead of throwing.
  const saved = safeGetItem('custos_lang');
  if (saved && SUPPORTED_LANGS.includes(saved as SupportedLang)) {
    return saved as SupportedLang;
  }
  // Detect from browser
  const browserLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage || 'en';
  // Exact match first
  if (SUPPORTED_LANGS.includes(browserLang as SupportedLang)) {
    return browserLang as SupportedLang;
  }
  // Prefix match (e.g. "ja-JP" → "ja", "zh-Hant" → "zh-TW")
  const prefix = browserLang.split('-')[0].toLowerCase();
  if (prefix === 'zh') {
    // zh-TW, zh-Hant → 繁體; zh-CN, zh-Hans → 简体
    if (browserLang.includes('TW') || browserLang.includes('Hant') || browserLang.includes('HK')) return 'zh-TW';
    return 'zh-CN';
  }
  const match = SUPPORTED_LANGS.find(l => l.toLowerCase().startsWith(prefix));
  return match ?? 'en';
}


@Component({
  selector: 'app-sidebar',
  imports: [TranslateModule, MatMenuModule, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  readonly currentRoute = input<string>('/rollock');
  readonly isOpen = input<boolean>(false);
  readonly navigate = output<string>();
  readonly walletAction = output<string>();
  readonly closed = output<void>();

  private readonly translate = inject(TranslateService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly clipboard = inject(Clipboard);
  readonly walletService = inject(WalletService);
  private readonly walletStore = inject(WalletStore);
  private readonly auth = inject(AuthService);
  private readonly predictionStore = inject(PredictionStore);
  private readonly portfolioStore = inject(PortfolioStore);

  /**
   * Invite link is only meaningful once the user has logged in — we use their
   * `f_user_info` (Custos-side account id) so referrals tie back to the same
   * account even if the wallet changes. Before login we fall back to a safe
   * null-ish string so any UI bound to this still renders something sane.
   */
  readonly inviteLink = computed(() => {
    const url = this.auth.inviteUrl();
    if (url) return url;
    const base = isPlatformBrowser(this.platformId)
      ? window.location.origin
      : 'https://custos.finance';
    return base;
  });
  readonly inviteCopied = signal(false);

  /** Expose auth state to the template so we can render sign-in status. */
  readonly isLoggedIn = computed(() => this.auth.isLoggedIn());
  readonly isLoggingIn = computed(() => this.auth.isLoggingIn());
  readonly authError = computed(() => this.auth.error());

  readonly isDark = signal(false);
  readonly langMenuOpen = signal(false);
  readonly themeMenuOpen = signal(false);
  readonly currentLang = signal<SupportedLang>(detectBrowserLang());
  readonly currentLangLabel = computed(() => LANG_LABELS[this.currentLang()] ?? this.currentLang());

  readonly langOptions: { value: SupportedLang; label: string }[] = SUPPORTED_LANGS.map(l => ({
    value: l,
    label: LANG_LABELS[l],
  }));

  readonly chainName = computed(() => {
    const chainId = this.walletService.chainId();
    if (!chainId) return '';
    switch (chainId) {
      case 56: return 'BNB Chain';
      case 97: return 'BSC Testnet';
      case 11155111: return 'Sepolia';
      case 1: return 'Ethereum';
      default: return `Chain ${chainId}`;
    }
  });

  readonly navItems: NavItem[] = [
    { label: 'NAV.ROLLOCK', route: '/rollock', icon: 'assets/icons/nav-rollocker.svg' },
    { label: 'NAV.PREDICTIONS', route: '/predictions', icon: 'assets/icons/nav-predictions.svg' },
    { label: 'NAV.TRUSTLOCK', route: 'https://lock.gopluslabs.io/legacy?type=v3', icon: 'assets/icons/nav-trustlocker.svg', external: true },
  ];

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      // Apply detected/saved language
      const detectedLang = this.currentLang();
      this.translate.use(detectedLang);

      // Theme: check localStorage first, then system preference
      const savedTheme = safeGetItem('custos_theme');
      let theme: 'light' | 'dark';
      if (savedTheme === 'light' || savedTheme === 'dark') {
        theme = savedTheme;
      } else {
        // Follow system preference
        theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      document.documentElement.setAttribute('data-theme', theme);
      this.isDark.set(theme === 'dark');
    }
  }

  isActive(route: string): boolean {
    return this.currentRoute().startsWith(route);
  }

  selectLang(lang: SupportedLang): void {
    this.translate.use(lang);
    this.currentLang.set(lang);
    this.langMenuOpen.set(false);
    // Persist user choice
    safeSetItem('custos_lang', lang);
  }

  setTheme(mode: 'light' | 'dark'): void {
    this.isDark.set(mode === 'dark');
    this.themeMenuOpen.set(false);
    if (isPlatformBrowser(this.platformId)) {
      document.documentElement.setAttribute('data-theme', mode);
      safeSetItem('custos_theme', mode);
    }
  }

  onNavClick(item: NavItem): void {
    if (item.external) {
      window.open(item.route, '_blank', 'noopener,noreferrer');
    } else {
      this.navigate.emit(item.route);
    }
    this.closed.emit();
  }

  onDisconnect(): void {
    this.walletService.disconnect();
    this.predictionStore.clear();
    this.portfolioStore.clear();
  }

  async onConnect(): Promise<void> {
    try {
      await this.walletService.connect('metamask');
      this.walletStore.refreshAll();
      // Sign in to the Custos backend so invite links / user-scoped APIs
      // work immediately after connect. No-op if a valid cached session
      // already exists for this address.
      await this.auth.ensureLoggedIn();
    } catch (err) {
      console.error('[Sidebar] Wallet connect failed:', err);
    }
  }

  /**
   * #I4: explicit retry for the case where the user cancelled the first
   * signing prompt (or the upstream errored). Wallet stays connected,
   * only the Custos login re-runs. No-op if already logged in.
   */
  async retryLogin(): Promise<void> {
    if (this.auth.isLoggingIn() || this.auth.isLoggedIn()) return;
    try {
      await this.auth.login();
    } catch (err) {
      console.error('[Sidebar] retry login failed:', err);
    }
  }

  copyInviteLink(): void {
    this.clipboard.copy(this.inviteLink());
    this.inviteCopied.set(true);
    setTimeout(() => this.inviteCopied.set(false), 2000);
  }
}
